import { useActionBarContext } from "@/components/contexts/action-bar-context";
import {
  DocumentationCard,
  DocumentationCardButtonGroup,
  DocumentationCardContent,
  DocumentationCardContentBadge,
  DocumentationCardHeader,
  DocumentationCardItem,
  DocumentationCardItemButtonGroup,
  DocumentationCardItemHeader,
  DocumentationCardItemTitle,
  DocumentationCardTitle,
} from "@/components/documentation-card";
import {
  ActiveOperationItem,
  SetActiveItem,
} from "@/components/module-api-editor/editor-preview-operation-active-item";
import {
  EditorInputProps,
  WorkspaceRole,
} from "@/components/module-api-editor/types";
import {
  AddButton,
  AddLabelButton,
  DeleteButton,
  EditButton,
} from "@/components/module-visual-editor/shared-components";
import { TypeBadge } from "@/components/special-badges";
import { useRequestBody } from "@/hooks/use-request-body";
import { NoRefsHereError } from "@/lib/errors";
import {
  getFirstSchemaFromContentObject,
  getPristineContentObject,
} from "@/lib/oas-tools/oas-schema-utils";
import { isReference } from "@/lib/oas-tools/oas-tag-helpers";
import { OASRequestBodyObject, SupportedHTTPVerbs } from "@/lib/types";
import { canEdit } from "@/lib/utils";
import { useMemo } from "react";

const NOT_SUPPORTED_FOR_REQUEST_BODY = ["GET", "HEAD", "DELETE"];

export function RequestBodyCard({
  operationId,
  method,
  value,
  onChange,
  workspaceRole,
  setActiveItem,
  activeItem,
}: {
  operationId: string;
  method: SupportedHTTPVerbs;
  workspaceRole: WorkspaceRole;
  setActiveItem: SetActiveItem;
  activeItem: ActiveOperationItem | undefined;
} & EditorInputProps) {
  const { getRequestBody, remove } = useRequestBody({ value, onChange });
  const [, actionBarDispatch] = useActionBarContext();

  const notSupported = NOT_SUPPORTED_FOR_REQUEST_BODY.includes(
    method.toUpperCase()
  );

  const requestBody = useMemo(() => {
    const found = getRequestBody({ operationId });
    if (!found) return undefined;
    if (isReference(found)) throw new NoRefsHereError();
    return found;
  }, [getRequestBody, operationId]);

  const schema = requestBody?.content
    ? getFirstSchemaFromContentObject(requestBody?.content)
    : getPristineContentObject();

  const handleEdit = (requestBody: OASRequestBodyObject) => {
    actionBarDispatch({
      type: "SET_PAGE",
      payload: {
        name: "request-body-edit",
        context: {
          operationId,
          requestBody,
        },
      },
    });
  };

  const handleAdd = () => {
    actionBarDispatch({
      type: "SET_PAGE",
      payload: {
        name: "request-body-add",
        context: {
          operationId,
        },
      },
    });
  };

  const handleRemove = () => {
    // IMPORTANT: Reset active item since path to `QuickEditor` will become
    // invalid and will make editor crash.
    setActiveItem(undefined);
    remove({ operationId });
  };

  if (notSupported) return null;

  const isEditor = canEdit(workspaceRole);
  if (!isEditor && !requestBody) return null;

  return (
    <DocumentationCard>
      <DocumentationCardHeader
        btnGroup={
          isEditor && !requestBody ? <AddButton onClick={handleAdd} /> : null
        }
      >
        <DocumentationCardTitle>Request body</DocumentationCardTitle>
      </DocumentationCardHeader>
      <DocumentationCardContent>
        {requestBody && (
          <DocumentationCardItem
            isSelected={activeItem?.type === "requestBody"}
            key={`${operationId}-${method}-requestBody`}
            onClick={() => setActiveItem({ type: "requestBody" })}
            isLast
          >
            <DocumentationCardItemHeader>
              <div className="flex gap-2">
                <TypeBadge
                  schema={schema}
                  forceColor="ghost"
                  isRequired={requestBody.required}
                />
                <DocumentationCardContentBadge
                  contentObject={requestBody.content}
                />
                <DocumentationCardItemTitle>Body</DocumentationCardItemTitle>
              </div>
              {isEditor && (
                <DocumentationCardItemButtonGroup>
                  <EditButton onClick={() => handleEdit(requestBody)}>
                    Edit request body
                  </EditButton>
                  <DeleteButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove();
                    }}
                  >
                    Delete request body
                  </DeleteButton>
                </DocumentationCardItemButtonGroup>
              )}
            </DocumentationCardItemHeader>
          </DocumentationCardItem>
        )}
        {!requestBody && isEditor && (
          <DocumentationCardButtonGroup className="mt-2">
            <AddLabelButton onClick={handleAdd} />
          </DocumentationCardButtonGroup>
        )}
      </DocumentationCardContent>
    </DocumentationCard>
  );
}
